import React, { useEffect, useRef, useState } from 'react';
import { Download, FileText, FileSpreadsheet, Check } from 'lucide-react';
import api from '../../utils/api';
import './exportMenu.css';

/**
 * Reusable "Export" dropdown for the Attendance + Marks pages.
 *
 * Props:
 *   endpoint  — "/attendance/export" | "/marks/export"
 *   role      — current user role (used to gate "whole school")
 *   scopes    — array of { id, label, params, hint? } options to show
 *   baseName  — hint for the downloaded filename
 *
 * Each scope supplies its own params object (studentId/class/section/dates).
 * We fetch the endpoint with the user's auth token (via api.ts interceptor),
 * turn the Blob into an object URL, and trigger a browser download.
 */
export default function ExportMenu({ endpoint, role, scopes, baseName = 'export' }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);   // id of the scope+format currently downloading
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const download = async (scope, format) => {
    const key = `${scope.id}-${format}`;
    setBusy(key);
    setErr(null);
    try {
      const params = { ...(scope.params || {}), scope: scope.id, format };
      const res = await api.get(endpoint, { params, responseType: 'blob' });

      const cd = res.headers['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/.exec(cd);
      const name = match?.[1] || `${baseName}-${scope.id}.${format}`;

      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      setDone(key);
      setTimeout(() => setDone((d) => (d === key ? null : d)), 1500);
    } catch (e) {
      // Blob responses can't be JSON-parsed automatically by axios; try manually.
      let msg = 'Export failed';
      try {
        const text = await e?.response?.data?.text?.();
        if (text) { try { msg = JSON.parse(text).message || msg; } catch { msg = text; } }
      } catch { /* fall through */ }
      setErr(msg);
      setTimeout(() => setErr(null), 3000);
    } finally {
      setBusy(null);
    }
  };

  const visibleScopes = scopes.filter((s) => {
    if (s.id === 'school' && role !== 'admin') return false;
    return !s.disabled;
  });

  return (
    <div className="exp-wrap" ref={ref}>
      <button
        className="exp-trigger"
        onClick={() => setOpen((o) => !o)}
        disabled={visibleScopes.length === 0}
      >
        <Download size={15} /> Export
      </button>

      {open && (
        <div className="exp-menu" role="menu">
          {visibleScopes.map((scope) => (
            <div key={scope.id} className="exp-group">
              <div className="exp-group-label">{scope.label}</div>
              {scope.hint && <div className="exp-group-hint">{scope.hint}</div>}
              <div className="exp-row">
                <button
                  className="exp-btn"
                  onClick={() => download(scope, 'csv')}
                  disabled={busy !== null}
                >
                  {done === `${scope.id}-csv`
                    ? <Check size={14} />
                    : <FileSpreadsheet size={14} />}
                  CSV
                </button>
                <button
                  className="exp-btn"
                  onClick={() => download(scope, 'pdf')}
                  disabled={busy !== null}
                >
                  {done === `${scope.id}-pdf`
                    ? <Check size={14} />
                    : <FileText size={14} />}
                  PDF
                </button>
              </div>
            </div>
          ))}
          {err && <div className="exp-err">{err}</div>}
        </div>
      )}
    </div>
  );
}
